import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Maps Slack emoji shortcodes (:name:) to Unicode emoji.
 * Covers common Slack default emojis; unknown codes are left as :code:.
 */
const SLACK_EMOJI_MAP: Record<string, string> = {
  slightly_smiling_face: '🙂',
  smile: '😄',
  grin: '😁',
  wave: '👋',
  pray: '🙏',
  muscle: '💪',
  thumbsup: '👍',
  thumbsdown: '👎',
  eyes: '👀',
  heart: '❤️',
  '+1': '👍',
  '-1': '👎',
  skin_tone_2: '',
  skin_tone_3: '',
  skin_tone_4: '',
  skin_tone_5: '',
  'skin-tone-2': '',
  'skin-tone-3': '',
  'skin-tone-4': '',
  'skin-tone-5': '',
  'skin-tone-6': '',
  smiley: '😃',
  wink: '😉',
  blush: '😊',
  yum: '😋',
  thinking_face: '🤔',
  neutral_face: '😐',
  expressionless: '😑',
  rolling_on_the_floor_laughing: '🤣',
  joy: '😂',
  sob: '😭',
  angry: '😠',
  star: '⭐',
  fire: '🔥',
  check: '✅',
  x: '❌',
  warning: '⚠️',
  bulb: '💡',
  rocket: '🚀',
  tada: '🎉',
  clap: '👏',
  raised_hands: '🙌',
  party_popper: '🎉',
  sparkles: '✨',
};

/**
 * Replaces Slack-style :shortcode: with Unicode emoji in message text.
 */
export function formatSlackTextWithEmoji(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/:([a-z0-9_+-]+):/gi, (_, code) => {
    const key = code.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(SLACK_EMOJI_MAP, key)) {
      return SLACK_EMOJI_MAP[key];
    }
    return `:${code}:`;
  });
}

const EXCERPT_HIGHLIGHT_BG = '#FFFDD4';

/** Slack link: <url> or <url|label>. GFM autolinks <url>; we convert both to markdown. */
const SLACK_LINK_REGEX = /<([^>|]+)(?:\|([^>]*))?>/g;

/**
 * Converts Slack formatting to Markdown: <url> / <url|label> → links, *text* → **bold**.
 */
export function slackToMarkdown(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return (
    text
      // <url|label> → [label](url); <url> → <url> for GFM autolink (add scheme if missing)
      .replace(SLACK_LINK_REGEX, (_, urlPart: string, labelPart: string | undefined) => {
        const url = urlPart.trim();
        const href = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
        if (labelPart != null && labelPart.trim() !== '') {
          const label = labelPart.trim().replace(/\]/g, '\\]');
          return `[${label}](${href})`;
        }
        return `<${href}>`;
      })
      // Slack bold *text* → Markdown **text**
      .replace(/\*([^*]+)\*/g, '**$1**')
  );
}

function normalizeForMatch(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function findExcerptRange(text: string, excerpt: string): [number, number] | null {
  const exact = text.indexOf(excerpt);
  if (exact >= 0) return [exact, exact + excerpt.length];
  const nText = normalizeForMatch(text);
  const nExcerpt = normalizeForMatch(excerpt);
  if (!nExcerpt || !nText.includes(nExcerpt)) return null;
  const nStart = nText.indexOf(nExcerpt);
  const nEnd = nStart + nExcerpt.length;
  let nIdx = 0;
  let origStart = -1;
  let origEnd = -1;
  for (let i = 0; i < text.length; i++) {
    const isSpace = /\s/.test(text[i]);
    const prevIsSpace = i > 0 && /\s/.test(text[i - 1]);
    if (!isSpace || !prevIsSpace) nIdx++;
    if (nIdx === nStart && origStart < 0) origStart = i;
    if (nIdx === nEnd && origEnd < 0) origEnd = i;
  }
  if (origEnd < 0) origEnd = text.length;
  return origStart >= 0 ? [origStart, origEnd] : null;
}

const slackMarkdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1264a3', textDecoration: 'underline' }}>
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
};

/**
 * Format Slack message text: emoji, Slack→Markdown (links + bold), then render with ReactMarkdown.
 * If excerptTexts are provided, splits on the first matching excerpt and wraps that segment in <mark>.
 */
export function formatSlackMessageText(
  text: string,
  excerptTexts: string[] | undefined
): (string | React.ReactElement)[] {
  if (!text) return [];
  const withEmoji = formatSlackTextWithEmoji(text);
  const toMd = (t: string) => slackToMarkdown(t);
  const renderMd = (md: string) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={slackMarkdownComponents}>
      {md}
    </ReactMarkdown>
  );

  if (!excerptTexts?.length) {
    return [renderMd(toMd(withEmoji))];
  }
  const excerpt = excerptTexts.find(
    (ex) => withEmoji.indexOf(ex) >= 0 || normalizeForMatch(withEmoji).includes(normalizeForMatch(ex))
  );
  if (!excerpt) {
    return [renderMd(toMd(withEmoji))];
  }
  const range = findExcerptRange(withEmoji, excerpt);
  if (!range) {
    return [renderMd(toMd(withEmoji))];
  }
  const [start, end] = range;
  const before = withEmoji.slice(0, start);
  const match = withEmoji.slice(start, end);
  const after = withEmoji.slice(end);
  const segments: (string | React.ReactElement)[] = [];
  if (before) segments.push(React.cloneElement(renderMd(toMd(before)), { key: 'before' }));
  if (match) {
    segments.push(
      <mark key="excerpt" style={{ backgroundColor: EXCERPT_HIGHLIGHT_BG, padding: '0 1px', borderRadius: 2 }}>
        {renderMd(toMd(match))}
      </mark>
    );
  }
  if (after) segments.push(React.cloneElement(renderMd(toMd(after)), { key: 'after' }));
  return segments.length > 0 ? segments : [renderMd(toMd(withEmoji))];
}
